import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Accessibility as AccessibilityIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  MonetizationOn as MoneyIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const ProposalGenerator = ({ business, websiteAnalysis }) => {
  const [selectedPackage, setSelectedPackage] = useState('complete');
  const [customPrice, setCustomPrice] = useState('');
  const [copiedText, setCopiedText] = useState('');

  if (!business || !websiteAnalysis) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Analysez d'abord un site web pour générer la proposition
        </Typography>
      </Box>
    );
  }

  const packages = {
    basic: {
      name: 'Pack Essentiel',
      price: '2 500€',
      duration: '2-3 semaines',
      description: 'Correction des problèmes critiques',
      features: [
        'Optimisation des performances',
        'Sécurisation HTTPS',
        'Optimisation SEO de base',
        'Responsive design mobile',
        'Correction des erreurs critiques'
      ],
      deliverables: [
        'Site web optimisé',
        "Rapport d'optimisation",
        'Formation utilisateur (1h)',
        'Support 1 mois'
      ]
    },
    standard: {
      name: 'Pack Professionnel',
      price: '5 000€',
      duration: '4-6 semaines',
      description: 'Optimisation complète du site',
      features: [
        'Tout du pack Essentiel',
        'Optimisation SEO avancée',
        'Design responsive complet',
        'Optimisation e-commerce',
        'Intégration analytics',
        'Optimisation des conversions'
      ],
      deliverables: [
        'Site web entièrement optimisé',
        'Audit SEO détaillé',
        'Plan de maintenance',
        'Formation utilisateur (2h)',
        'Support 3 mois'
      ]
    },
    complete: {
      name: 'Pack Excellence',
      price: '8 000€',
      duration: '6-8 semaines',
      description: 'Refonte complète et optimisation',
      features: [
        'Tout du pack Professionnel',
        'Refonte design complète',
        'Optimisation UX/UI',
        'Intégration CRM',
        'Automatisation marketing',
        'Formation équipe complète'
      ],
      deliverables: [
        'Site web refait à neuf',
        'Stratégie marketing digitale',
        'Plan de croissance',
        'Formation équipe (4h)',
        'Support 6 mois',
        'Accompagnement stratégique'
      ]
    },
    custom: {
      name: 'Pack Sur Mesure',
      price: customPrice || 'Sur devis',
      duration: 'À définir',
      description: 'Solution personnalisée selon vos besoins',
      features: [
        'Analyse approfondie de vos besoins',
        'Solution sur mesure',
        'Intégrations spécifiques',
        'Accompagnement personnalisé'
      ],
      deliverables: [
        'Solution personnalisée',
        'Documentation complète',
        'Formation sur mesure',
        'Support dédié'
      ]
    }
  };

  const generateProposal = () => {
    const selectedPackageData = packages[selectedPackage];
    const criticalIssues = websiteAnalysis.criticalIssues || 0;
    const overallScore = websiteAnalysis.overallScore || 0;
    
    return {
      title: `Proposition Commerciale - ${business.name}`,
      subtitle: `Optimisation de votre site web ${business.website}`,
      executiveSummary: `Suite à l'analyse approfondie de votre site web ${business.website}, nous avons identifié ${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} et un score global de ${overallScore}/100 qui nécessitent une intervention immédiate pour préserver votre business et maximiser vos résultats.`,
      
      currentSituation: {
        score: overallScore,
        criticalIssues,
        problems: [
          'Performance dégradée impactant l\'expérience utilisateur',
          'Sécurité insuffisante exposant vos données',
          'Visibilité Google limitée',
          'Expérience mobile non optimisée',
          'Taux de conversion sous-optimal'
        ]
      },
      
      proposedSolution: {
        package: selectedPackageData,
        benefits: [
          `Augmentation des conversions de +150% à +300%`,
          `Amélioration du positionnement Google`,
          `Expérience utilisateur optimisée`,
          `Sécurité renforcée`,
          `ROI positif en 3-6 mois`
        ],
        timeline: selectedPackageData.duration,
        investment: selectedPackageData.price
      },
      
      methodology: [
        'Phase 1 : Audit approfondi et planification (1 semaine)',
        'Phase 2 : Développement et optimisation (2-4 semaines)',
        'Phase 3 : Tests et validation (1 semaine)',
        'Phase 4 : Mise en ligne et formation (1 semaine)'
      ],
      
      guarantee: {
        satisfaction: '100% satisfaction garantie ou remboursé',
        performance: 'Amélioration des performances garantie',
        support: 'Support technique inclus',
        maintenance: 'Maintenance préventive incluse'
      }
    };
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const downloadProposal = () => {
    const proposal = generateProposal();
    const content = `PROPOSITION COMMERCIALE - ${business.name}

${proposal.title}
${proposal.subtitle}

RÉSUMÉ EXÉCUTIF
${proposal.executiveSummary}

SITUATION ACTUELLE
• Score global du site : ${proposal.currentSituation.score}/100
• Problèmes critiques identifiés : ${proposal.currentSituation.criticalIssues}
• Impact sur le business : Perte de clients et de revenus

PROBLÈMES IDENTIFIÉS
${proposal.currentSituation.problems.map(p => `• ${p}`).join('\n')}

SOLUTION PROPOSÉE
Pack choisi : ${proposal.proposedSolution.package.name}
Description : ${proposal.proposedSolution.package.description}
Durée : ${proposal.proposedSolution.timeline}
Investissement : ${proposal.proposedSolution.investment}

FONCTIONNALITÉS INCLUSES
${proposal.proposedSolution.package.features.map(f => `• ${f}`).join('\n')}

LIVRABLES
${proposal.proposedSolution.package.deliverables.map(d => `• ${d}`).join('\n')}

BÉNÉFICES ATTENDUS
${proposal.proposedSolution.benefits.map(b => `• ${b}`).join('\n')}

MÉTHODOLOGIE
${proposal.methodology.map(m => `• ${m}`).join('\n')}

GARANTIES
• ${proposal.guarantee.satisfaction}
• ${proposal.guarantee.performance}
• ${proposal.guarantee.support}
• ${proposal.guarantee.maintenance}

PROCHAINES ÉTAPES
1. Validation de la proposition
2. Signature du contrat
3. Démarrage du projet
4. Livraison et formation

Contact : [Votre nom] - [Votre téléphone] - [Votre email]
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposition-${business.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const proposal = generateProposal();
  const selectedPackageData = packages[selectedPackage];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon color="primary" />
        Générateur de Proposition Commerciale
      </Typography>

      {/* Résumé de la situation */}
      <Card sx={{ mb: 3, bgcolor: 'warning.light', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 Situation Actuelle - {business.name}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="error.light">
                {proposal.currentSituation.score}/100
              </Typography>
              <Typography variant="body2">Score Global</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="error.light">
                {proposal.currentSituation.criticalIssues}
              </Typography>
              <Typography variant="body2">Problèmes Critiques</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="success.light">
                +150%
              </Typography>
              <Typography variant="body2">Gain Potentiel</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="info.light">
                3-6 mois
              </Typography>
              <Typography variant="body2">ROI Attendu</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sélection du pack */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Choisir un Pack
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Pack proposé</InputLabel>
                <Select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  label="Pack proposé"
                >
                  {Object.entries(packages).map(([key, pack]) => (
                    <MenuItem key={key} value={key}>
                      <Box>
                        <Typography variant="subtitle1">{pack.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pack.price} - {pack.duration}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedPackage === 'custom' && (
                <TextField
                  fullWidth
                  label="Prix personnalisé"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  placeholder="ex: 15 000€"
                  sx={{ mb: 2 }}
                />
              )}

              <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedPackageData.name}
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    {selectedPackageData.price}
                  </Typography>
                  <Typography variant="body2">
                    Durée : {selectedPackageData.duration}
                  </Typography>
                  <Typography variant="body2">
                    {selectedPackageData.description}
                  </Typography>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 Détails de la Proposition
              </Typography>

              {/* Fonctionnalités */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="primary" />
                  Fonctionnalités Incluses
                </Typography>
                <List dense>
                  {selectedPackageData.features.map((feature, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Livrables */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DownloadIcon color="primary" />
                  Livrables
                </Typography>
                <List dense>
                  {selectedPackageData.deliverables.map((deliverable, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={deliverable} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Méthodologie */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  Méthodologie
                </Typography>
                <List dense>
                  {proposal.methodology.map((phase, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          {index + 1}
                        </Typography>
                      </ListItemIcon>
                      <ListItemText primary={phase} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Garanties */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingIcon color="primary" />
                  Nos Garanties
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(proposal.guarantee).map(([key, guarantee]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <Chip
                        icon={<CheckIcon />}
                        label={guarantee}
                        color="success"
                        variant="outlined"
                        sx={{ width: '100%', justifyContent: 'flex-start' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Proposition complète */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📄 Proposition Commerciale Complète
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Résumé exécutif :
            </Typography>
            <Typography variant="body1" sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              {proposal.executiveSummary}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Bénéfices attendus :
            </Typography>
            <List dense>
              {proposal.proposedSolution.benefits.map((benefit, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <TrendingIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadProposal}
              size="large"
            >
              Télécharger la Proposition
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(proposal.executiveSummary, 'resume')}
              size="large"
            >
              Copier le Résumé
            </Button>
          </Box>
        </CardContent>
      </Card>

      {copiedText && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Résumé copié !
        </Alert>
      )}
    </Box>
  );
};

export default ProposalGenerator;